import React, { useState } from "react";
import { FaTrash, FaEye, FaEdit, FaCoins, FaUniversity, FaBitcoin, FaCog, FaBox } from "react-icons/fa";
import BaseModal from "./BaseModal";
import CashAccountForm from "./CashAccountForm";
import BankAccountForm from "./BankAccountForm";
import CryptoAccountForm from "./CryptoAccountForm";
import FixedAssetForm from "./FixedAssetForm";
import TradingAssetForm from "./TradingAssetForm";

const AssetModal = ({ 
  isOpen, 
  onClose, 
  asset, 
  modalType, // 'view', 'edit', 'delete'
  onUpdate,
  onDelete
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!asset) return null;

  const getAssetIcon = (type) => {
    switch (type) {
      case "cash":
        return <FaCoins className="w-5 h-5 text-green-500" />;
      case "bank":
        return <FaUniversity className="w-5 h-5 text-blue-500" />;
      case "crypto":
        return <FaBitcoin className="w-5 h-5 text-orange-500" />;
      case "fixed":
        return <FaCog className="w-5 h-5 text-purple-500" />;
      case "trading":
        return <FaBox className="w-5 h-5 text-emerald-500" />;
      default:
        return <FaBox className="w-5 h-5 text-slate-500" />;
    }
  };

  const getAssetTypeName = (type) => {
    switch (type) {
      case "cash":
        return "Cash Account";
      case "bank":
        return "Bank Account";
      case "crypto":
        return "Crypto Account";
      case "fixed":
        return "Fixed Asset";
      case "trading":
        return "Trading Asset";
      default:
        return "Asset";
    }
  };

  const getAssetDisplayName = (asset) => {
    switch (asset.type) {
      case "cash":
        return asset.name;
      case "bank":
        return asset.bank_name;
      case "crypto":
        return `${asset.exchange_name} - ${asset.token_name}`;
      case "fixed":
      case "trading":
        return asset.name;
      default:
        return "Unknown Asset";
    }
  };

  const handleSubmit = async (formData) => {
    setIsLoading(true);
    try {
      let endpoint = "";
      switch (asset.type) {
        case "cash":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/cash/${asset.id}`;
          break;
        case "bank":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/bank/${asset.id}`;
          break;
        case "crypto":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/crypto/${asset.id}`;
          break;
        case "fixed":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/fixed-assets/${asset.id}`;
          break;
        case "trading":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/trading-assets/${asset.id}`;
          break;
      }

      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        alert("Asset updated successfully!");
        onUpdate();
        onClose();
      } else {
        alert(data.error || "Failed to update asset");
      }
    } catch (error) {
      console.error("Error updating asset:", error);
      alert("Failed to update asset");
    }
    setIsLoading(false);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      let endpoint = "";
      switch (asset.type) {
        case "cash":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/cash/${asset.id}`;
          break;
        case "bank":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/bank/${asset.id}`;
          break;
        case "crypto":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/crypto/${asset.id}`;
          break;
        case "fixed":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/fixed-assets/${asset.id}`;
          break;
        case "trading":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/trading-assets/${asset.id}`;
          break;
      }

      const response = await fetch(endpoint, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        alert("Asset deleted successfully!");
        onDelete();
        onClose();
      } else {
        alert(data.error || "Failed to delete asset");
      }
    } catch (error) {
      console.error("Error deleting asset:", error);
      alert("Failed to delete asset");
    }
    setIsLoading(false);
  };

  const getModalTitle = () => {
    const icon = getAssetIcon(asset.type);
    const typeText = modalType === "view" ? "View" : modalType === "edit" ? "Edit" : "Delete";
    return (
      <div className="flex items-center">
        {icon}
        <span className="ml-2">{typeText} {getAssetTypeName(asset.type)}</span>
      </div>
    );
  };

  const renderEditForm = () => {
    const commonProps = {
      initialData: asset,
      onSubmit: handleSubmit,
      isLoading,
      isEdit: true
    };

    switch (asset.type) {
      case "cash":
        return <CashAccountForm {...commonProps} />;
      case "bank":
        return <BankAccountForm {...commonProps} />;
      case "crypto":
        return <CryptoAccountForm {...commonProps} />;
      case "fixed":
        return <FixedAssetForm {...commonProps} />;
      case "trading":
        return <TradingAssetForm {...commonProps} />;
      default:
        return <div>Unknown asset type</div>;
    }
  };

  const renderViewContent = () => (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-medium text-slate-600">
          Asset Type
        </p>
        <p className="text-lg font-semibold text-slate-800">
          {getAssetTypeName(asset.type)}
        </p>
      </div>

      {/* Dynamic field rendering based on asset type */}
      {asset.type === "cash" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Name
              </p>
              <p className="text-slate-800">{asset.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">
                Amount
              </p>
              <p className="text-slate-800">
                {parseFloat(asset.amount || 0).toFixed(2)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Max Limit
              </p>
              <p className="text-slate-800">
                {parseFloat(asset.max_limit || 0).toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">
                Min Limit
              </p>
              <p className="text-slate-800">
                {parseFloat(asset.min_limit || 0).toFixed(2)}
              </p>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-600">
              Location
            </p>
            <p className="text-slate-800">
              {asset.location_name || "N/A"}
            </p>
          </div>
          {asset.description && (
            <div>
              <p className="text-sm font-medium text-slate-600">
                Description
              </p>
              <p className="text-slate-800">
                {asset.description}
              </p>
            </div>
          )}
        </>
      )}

      {asset.type === "bank" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Bank Name
              </p>
              <p className="text-slate-800">
                {asset.bank_name}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">
                Account Type
              </p>
              <p className="text-slate-800">
                {asset.account_type}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Account Number
              </p>
              <p className="text-slate-800">
                {asset.account_number}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">
                Holder Name
              </p>
              <p className="text-slate-800">
                {asset.holder_name}
              </p>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-600">
              Bank Address
            </p>
            <p className="text-slate-800">
              {asset.bank_address}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-600">
              Amount
            </p>
            <p className="text-slate-800">
              {parseFloat(asset.amount || 0).toFixed(2)}
            </p>
          </div>
        </>
      )}

      {asset.type === "crypto" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Exchange Name
              </p>
              <p className="text-slate-800">
                {asset.exchange_name}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">
                Token Name
              </p>
              <p className="text-slate-800">
                {asset.token_name}
              </p>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-600">
              Number of Tokens
            </p>
            <p className="text-slate-800">
              {parseFloat(asset.no_of_tokens || 0).toFixed(8)} {asset.token_name}
            </p>
          </div>
        </>
      )}

      {(asset.type === "fixed" || asset.type === "trading") && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Name
              </p>
              <p className="text-slate-800">{asset.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">
                Category
              </p>
              <p className="text-slate-800">
                {asset.category_path || "N/A"}
              </p>
            </div>
          </div>
          {asset.description && (
            <div>
              <p className="text-sm font-medium text-slate-600">
                Description
              </p>
              <p className="text-slate-800">
                {asset.description}
              </p>
            </div>
          )}
          {asset.type === "trading" && asset.formula && (
            <div>
              <p className="text-sm font-medium text-slate-600">
                Formula
              </p>
              <p className="text-slate-800">
                {asset.formula}
              </p>
            </div>
          )}
        </>
      )}

      {/* Contact persons for current assets */}
      {asset.contacts && asset.contacts.length > 0 && (
        <div>
          <p className="text-sm font-medium text-slate-600 mb-2">
            Contact Persons
          </p>
          <div className="space-y-2">
            {asset.contacts.map((contact, index) => (
              <div
                key={index}
                className="bg-slate-50 p-3 rounded-lg"
              >
                <p className="text-sm font-medium">
                  {contact.name}
                </p>
                <p className="text-sm text-slate-600">
                  {contact.contact}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t pt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-slate-600">
              Created
            </p>
            <p className="text-slate-800">
              {new Date(asset.created_at).toLocaleDateString()}
            </p>
          </div>
          {asset.updated_at && (
            <div>
              <p className="text-sm font-medium text-slate-600">
                Updated
              </p>
              <p className="text-slate-800">
                {new Date(asset.updated_at).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderDeleteContent = () => (
    <div className="text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
        <FaTrash className="h-6 w-6 text-red-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Delete Asset
      </h3>
      <p className="text-sm text-gray-500 mb-6">
        Are you sure you want to delete "{getAssetDisplayName(asset)}"? This action cannot be undone.
      </p>
      <div className="flex space-x-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          onClick={handleDelete}
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          {isLoading ? "Deleting..." : "Delete"}
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (modalType) {
      case "view":
        return renderViewContent();
      case "edit":
        return renderEditForm();
      case "delete":
        return renderDeleteContent();
      default:
        return <div>Unknown modal type</div>;
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      maxWidth={modalType === "delete" ? "max-w-lg" : "max-w-2xl"}
    >
      {renderContent()}
    </BaseModal>
  );
};

export default AssetModal;



